import Button from "@/components/Button"
import TextInput from "@/components/forms/TextInput"
import AccountSidebar from "@/components/interface/AccountSidebar"
import Layout from "@/components/layouts/DefaultLayout"
import Settings, { SettingsField, SettingsFooter } from "@/components/Settings"
import { ACCOUNT_URL } from "@/constants"
import { AuthError, useAuth } from "@/contexts/auth"
import { useUserSettings } from "@/hooks/user-settings"
import { ErrorMessage, Field, Form, Formik } from "formik"
import { useEffect } from "react"
import { Outlet as SubPage, useLocation, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import * as yup from "yup"
import { Checkbox } from "@nextui-org/checkbox"
import { ReactComponent as CheckIcon } from "@/icons/check-icon.svg"

const AccountPage = () => {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/")
      toast.warning("You must be logged in")
    }
  }, [])

  return (
    <Layout>
      <div className="flex h-full">
        <AccountSidebar />
        <div className="flex h-full flex-1 flex-col gap-y-6 overflow-y-auto px-14 py-4">
          {location.pathname === ACCOUNT_URL ? <MainPage /> : <SubPage />}
        </div>
      </div>
    </Layout>
  )
}

const MainPage = () => {
  const { deleteAccount, updateAccount, user, logout } = useAuth()
  const { notifyUpdates, updateNotifyUpdates } = useUserSettings()
  const navigate = useNavigate()

  /**
   * Delete user account, logout and then redirect to home page
   */
  const onDeleteAccountClicked = async () => {
    try {
      const confirm = await window.confirm("Are you sure you want to delete your account?")
      if (confirm) {
        await deleteAccount()
        await logout()
        navigate("/")
      }
    } catch (error) {
      toast.error("Error deleting account")
    }
  }

  /**
   * Submit changes to account
   **/
  const onSubmit = async (values) => {
    try {
      await updateAccount(values)
      toast.success("Account updated")
    } catch (error) {
      console.error(error.errors)
      if (error instanceof AuthError) {
        toast.error("Unexpected error. Please contact support.")
      } else {
        toast.error("Unexpected error. Please contact support.")
      }
    }
  }

  return (
    <>
      <Settings title="Account Settings">
        {user && (
          <Formik
            initialValues={{ name: user.name, email: user.email }}
            validationSchema={yup.object({
              name: yup.string().required("Name or username is required"),
              email: yup.string().email().required("Email is required"),
            })}
            onSubmit={onSubmit}
          >
            <Form>
              <div className="space-y-6">
                <SettingsField label="Username" htmlFor="name" className="items-start">
                  <div className="mt-2.5 flex-1">
                    <Field as={TextInput} type="text" name="name" placeholder="Name" />
                    <ErrorMessage component="div" className="mt-1 text-xs text-red" name="name" />
                  </div>
                </SettingsField>
                <SettingsField label="Email" htmlFor="email" className="items-start">
                  <div className="mt-2.5 flex-1">
                    <Field as={TextInput} name="email" type="email" placeholder="Email" />
                    <ErrorMessage component="div" className="mt-1 text-xs text-red" name="email" />
                  </div>
                </SettingsField>
                <SettingsField
                  label="Update Notifications"
                  htmlFor="notify_updates"
                  className="items-start"
                >
                  <div className="mt-3 flex-1">
                    <Checkbox
                      isSelected={notifyUpdates}
                      onValueChange={updateNotifyUpdates}
                      classNames={{
                        wrapper: [
                          "bg-cod-gray border border-earth rounded-plus before:border-0 after:bg-transparent text-red mr-2 w-4.5 h-4.5",
                          "group-data-[selected=true]:bg-eclipse group-data-[selected=true]:border-darker-red",
                        ],
                        label: "font-bulo text-[13px] text-ghost",
                        icon: "transition-opacity duration-150 w-3",
                      }}
                      icon={<CheckIcon />}
                      disableAnimation={true}
                    >
                      Receive email notifications about Core and firmware updates
                    </Checkbox>
                  </div>
                </SettingsField>
                <SettingsFooter>
                  <div className="flex items-center gap-x-2">
                    <Button theme="blue" size="sm" type="submit">
                      Save Changes
                    </Button>
                    <Button theme="red" size="sm" onClick={onDeleteAccountClicked}>
                      Delete Account
                    </Button>
                  </div>
                </SettingsFooter>
              </div>
            </Form>
          </Formik>
        )}
      </Settings>
    </>
  )
}

export default AccountPage
